import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, Calendar, Clock, User, Settings, LogOut, 
  Users, MessageCircle, DollarSign, TrendingUp, Bell
} from 'lucide-react';
import { useUserAuth } from '../../context/UserAuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { fadeInUp, transitionMedium, createStaggerDelay } from '../../utils/animations';

// Get locale string from language code
const getLocale = (lang) => {
  const locales = { ko: 'ko-KR', ja: 'ja-JP', en: 'en-US' };
  return locales[lang] || locales.en;
};

const translations = {
  en: {
    welcome: 'Welcome back',
    dashboard: 'Teacher Dashboard',
    todaysClasses: "Today's Classes",
    noClasses: 'No classes scheduled for today',
    viewSchedule: 'View Full Schedule',
    overview: 'Overview',
    totalStudents: 'Total Students',
    classesThisMonth: 'Classes This Month',
    hoursTeaching: 'Hours Teaching',
    earnings: 'Earnings',
    quickActions: 'Quick Actions',
    manageSchedule: 'Manage Schedule',
    myStudents: 'My Students',
    classNotes: 'Class Notes',
    messages: 'Messages',
    settings: 'Settings',
    logout: 'Logout',
    pendingRequests: 'Pending Requests',
    noPendingRequests: 'No pending class requests',
    viewAll: 'View All'
  },
  ko: {
    welcome: '돌아오신 것을 환영합니다',
    dashboard: '선생님 대시보드',
    todaysClasses: '오늘 수업',
    noClasses: '오늘 예정된 수업이 없습니다',
    viewSchedule: '전체 일정 보기',
    overview: '개요',
    totalStudents: '전체 학생 수',
    classesThisMonth: '이번 달 수업',
    hoursTeaching: '수업 시간',
    earnings: '수입',
    quickActions: '빠른 작업',
    manageSchedule: '일정 관리',
    myStudents: '내 학생',
    classNotes: '수업 노트',
    messages: '메시지',
    settings: '설정',
    logout: '로그아웃',
    pendingRequests: '대기 중인 요청',
    noPendingRequests: '대기 중인 수업 요청이 없습니다',
    viewAll: '전체 보기'
  },
  ja: {
    welcome: 'おかえりなさい',
    dashboard: '講師ダッシュボード',
    todaysClasses: '本日のレッスン',
    noClasses: '本日予定のレッスンはありません',
    viewSchedule: 'スケジュール全体を見る',
    overview: '概要',
    totalStudents: '生徒数',
    classesThisMonth: '今月のレッスン',
    hoursTeaching: '指導時間',
    earnings: '収入',
    quickActions: 'クイックアクション',
    manageSchedule: 'スケジュール管理',
    myStudents: '生徒一覧',
    classNotes: 'レッスンノート',
    messages: 'メッセージ',
    settings: '設定',
    logout: 'ログアウト',
    pendingRequests: '保留中のリクエスト',
    noPendingRequests: '保留中のレッスンリクエストはありません',
    viewAll: 'すべて見る'
  }
};

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useUserAuth();
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Mock data - in production, this would come from the API
  const stats = {
    totalStudents: 0,
    classesThisMonth: 0,
    hoursTeaching: 0,
    earnings: '$0'
  };

  const todaysClasses = [];
  const pendingRequests = [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="teacher-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-slate-900">English Passion</Link>
              <span className="text-sm text-slate-500 hidden sm:inline" data-testid="dashboard-type">{t.dashboard}</span>
            </div>
            <div className="flex items-center gap-4">
              <button className="p-2 text-slate-500 hover:text-slate-900 transition-colors relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5" />
                {pendingRequests.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingRequests.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => navigate('/settings')}
                className="p-2 text-slate-500 hover:text-slate-900 transition-colors"
                data-testid="settings-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">{t.logout}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <motion.div
          initial={fadeInUp.initial}
          animate={fadeInUp.animate}
          transition={transitionMedium}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            {t.welcome}, {user?.name}! 👋
          </h1>
          <p className="text-slate-600">
            {new Date().toLocaleDateString(getLocale(language), {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={fadeInUp.initial}
          animate={fadeInUp.animate}
          transition={createStaggerDelay(1)}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <Users className="w-8 h-8 text-blue-500 mb-3" />
            <p className="text-2xl font-bold text-slate-900">{stats.totalStudents}</p>
            <p className="text-sm text-slate-500">{t.totalStudents}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <BookOpen className="w-8 h-8 text-green-500 mb-3" />
            <p className="text-2xl font-bold text-slate-900">{stats.classesThisMonth}</p>
            <p className="text-sm text-slate-500">{t.classesThisMonth}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <Clock className="w-8 h-8 text-amber-500 mb-3" />
            <p className="text-2xl font-bold text-slate-900">{stats.hoursTeaching}</p>
            <p className="text-sm text-slate-500">{t.hoursTeaching}</p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <DollarSign className="w-8 h-8 text-purple-500 mb-3" />
            <p className="text-2xl font-bold text-slate-900">{stats.earnings}</p>
            <p className="text-sm text-slate-500">{t.earnings}</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Today's Classes */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t.todaysClasses}</h2>
                <Link
                  to="/schedule"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t.viewSchedule} →
                </Link>
              </div>
              
              {todaysClasses.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t.noClasses}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todaysClasses.map((cls) => (
                    <div key={cls.id || cls.scheduled_time} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{cls.studentName}</p>
                        <p className="text-sm text-slate-500">{cls.time}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        cls.status === 'upcoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {cls.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Pending Requests */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">{t.pendingRequests}</h2>
                <Link
                  to="/requests"
                  className="text-sm text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {t.viewAll} →
                </Link>
              </div>
              
              {pendingRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">{t.noPendingRequests}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingRequests.map((req) => (
                    <div key={req.id || req.scheduled_time} className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{req.studentName}</p>
                        <p className="text-sm text-slate-500">{req.requestedTime}</p>
                      </div>
                      <div className="flex gap-2">
                        <button className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                          Accept
                        </button>
                        <button className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-300 transition-colors">
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900">{user?.name}</h3>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <div className="flex items-center justify-center gap-1 mt-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500 capitalize">{user?.role}</span>
                </div>
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm p-6"
            >
              <h3 className="font-semibold text-slate-900 mb-4">{t.quickActions}</h3>
              <div className="space-y-2">
                <Link
                  to="/schedule"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <Calendar className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.manageSchedule}</span>
                </Link>
                <Link
                  to="/students"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <Users className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.myStudents}</span>
                </Link>
                <Link
                  to="/notes"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <TrendingUp className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.classNotes}</span>
                </Link>
                <Link
                  to="/messages"
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  <MessageCircle className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-700">{t.messages}</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TeacherDashboard;
